// SF5 — what shoppers typed, account-wide.
//
// The word-root view is the part a term-by-term table cannot give: a word that
// wastes a little across a hundred terms is invisible on each row and obvious
// once the word is summed.
import React from 'react';
import s from '../AdsAnalytics.module.css';
import { money, num, ratio, LABEL_TONE } from '../format';

const VIEWS = [
  ['all', 'All terms'], ['converting', 'Converting'], ['high_performers', 'High performers'],
  ['waste', 'Waste'], ['branded', 'Branded'], ['generic', 'Generic'],
  ['competitor', 'Competitor'],
];

export default function SearchTerms({ data, loading, view, onView }) {
  if (loading && !data) return <div className={s.empty}>Loading…</div>;
  if (!data) return null;
  const currency = data.currency;

  return (
    <>
      <div className={s.chipRow}>
        {VIEWS.map(([id, label]) => (
          <button key={id} className={view === id ? s.chipActive : s.chip}
                  onClick={() => onView(id)}>{label}</button>
        ))}
      </div>

      <div className={s.wasteBanner}>
        <strong>{money(data.wasted, currency)}</strong> wasted in this period —
        spend on terms that sold nothing, or sold far above your target.
      </div>

      <div className={s.tileRow} style={{ marginTop: 12 }}>
        {['branded', 'generic', 'competitor'].map((kind) => (
          <div key={kind} className={`${s.tile} ${s.tileSmall}`}>
            <span className={s.tileLabel}>{kind}</span>
            <span className={s.tileValueSmall}>{money(data.mix?.[kind]?.spend, currency)}</span>
            <span className={s.tileNote}>
              ACOS {ratio(data.mix?.[kind]?.acos, data.mix?.[kind]?.acos_reason)}
            </span>
          </div>
        ))}
      </div>

      {!data.rows?.length ? (
        <div className={s.empty} style={{ marginTop: 14 }}>No terms for this view.</div>
      ) : (
        <div className={s.tableWrap} style={{ marginTop: 14 }}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Search term</th>
                <th className={s.right}>Spend</th><th className={s.right}>Ad sales</th>
                <th className={s.right}>ACOS</th><th className={s.right}>Orders</th>
                <th>Verdict</th><th className={s.right}>Waste</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.search_term}>
                  <td>
                    {/* A term that is an ASIN is a product page, and * is Amazon's
                        own mark for product-page placements. Both read as
                        gibberish unless named. */}
                    <span className={s.term}>
                      {r.is_placement ? 'Product pages (*)' : r.search_term}
                    </span>
                    {r.is_asin && <span className={s.tag}>a product page</span>}
                    {r.high_performer && <span className={s.tagGood}>high performer</span>}
                    <span className={s.tag}>{r.classification}</span>
                  </td>
                  <td className={s.right}>{money(r.spend, currency)}</td>
                  <td className={s.right}>{money(r.ad_sales, currency)}</td>
                  <td className={s.right}>{ratio(r.acos, r.acos_reason)}</td>
                  <td className={s.right}>{num(r.orders)}</td>
                  <td>
                    <span className={`${s.verdict} ${s[LABEL_TONE[r.label] || 'muted']}`}
                          title={r.why}>{r.label}</span>
                  </td>
                  <td className={s.right}>{r.waste ? money(r.waste, currency) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.roots?.length > 0 && (
        <section style={{ marginTop: 22 }}>
          <h4 className={s.sectionTitle}>Words, summed across every term carrying them</h4>
          <table className={s.subTable}>
            <thead>
              <tr>
                <th>Word root</th><th className={s.right}>Terms</th>
                <th className={s.right}>Spend</th><th className={s.right}>Ad sales</th>
                <th className={s.right}>ACOS</th>
              </tr>
            </thead>
            <tbody>
              {data.roots.slice(0, 25).map((r) => (
                <tr key={r.root}>
                  <td>{r.root}</td>
                  <td className={s.right}>{num(r.terms)}</td>
                  <td className={s.right}>{money(r.spend, currency)}</td>
                  <td className={s.right}>{money(r.ad_sales, currency)}</td>
                  <td className={s.right}>{ratio(r.acos, r.acos_reason)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <p className={s.hint} style={{ marginTop: 10 }}>{data.note}</p>
    </>
  );
}
